<script setup>
import { computed } from "vue";

const props = defineProps({
  variant: { type: String, default: "primary" }, // primary | secondary | ghost | inverse
  size: { type: String, default: "md" }, // sm | md | lg
  to: { type: [String, Object], default: null },
  href: { type: String, default: null },
  type: { type: String, default: "button" },
  block: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const tag = computed(() => {
  if (props.to) return "router-link";
  if (props.href) return "a";
  return "button";
});

const bindings = computed(() => {
  if (props.to) return { to: props.to };
  if (props.href) {
    return { href: props.href, target: "_blank", rel: "noopener noreferrer" };
  }
  return { type: props.type, disabled: props.disabled || props.loading };
});
</script>

<template>
  <component
    :is="tag"
    v-bind="bindings"
    class="btn"
    :class="[
      `btn--${variant}`,
      `btn--${size}`,
      { 'btn--block': block, 'is-loading': loading, sheen: variant === 'primary' },
    ]"
  >
    <span v-if="loading" class="spinner" aria-hidden="true" />
    <slot v-else name="icon" />
    <span class="btn__label"><slot /></span>
  </component>
</template>

<style scoped>
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border-radius: var(--radius-pill);
  font-weight: var(--weight-semibold);
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background-color var(--dur-fast) var(--ease-in-out),
    border-color var(--dur-fast) var(--ease-in-out),
    color var(--dur-fast) var(--ease-in-out),
    transform var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out);
}

.btn:hover:not(:disabled) {
  transform: translateY(-2px);
}

.btn:active:not(:disabled) {
  transform: translateY(0);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ------------------------------------------------------------- variants */

.btn--primary {
  background: var(--brand);
  color: var(--on-brand);
  box-shadow: var(--lift-1);
}
.btn--primary:hover:not(:disabled) {
  background: var(--brand-hover);
  box-shadow: var(--lift-2);
}
.btn--primary:active:not(:disabled) {
  background: var(--brand-press);
}

.btn--secondary {
  background: var(--surface);
  color: var(--ink);
  border-color: var(--line-strong);
}
.btn--secondary:hover:not(:disabled) {
  border-color: var(--brand);
  color: var(--brand);
  box-shadow: var(--lift-2);
}

.btn--ghost {
  background: transparent;
  color: var(--brand);
}
.btn--ghost:hover:not(:disabled) {
  background: var(--brand-soft);
}

.btn--inverse {
  background: var(--surface);
  color: var(--ink);
}
.btn--inverse:hover:not(:disabled) {
  background: var(--brand-soft);
  box-shadow: var(--lift-3);
}

/* ---------------------------------------------------------------- sizes */

.btn--sm {
  font-size: var(--text-sm);
  padding: var(--space-2) var(--space-4);
}

.btn--md {
  font-size: var(--text-base);
  padding: var(--space-3) var(--space-5);
}

.btn--lg {
  font-size: var(--text-lg);
  padding: var(--space-4) var(--space-7);
}

.btn--block {
  display: flex;
  inline-size: 100%;
}

.btn__label {
  display: inline-block;
}

.btn :deep(svg) {
  inline-size: 1.15em;
  block-size: 1.15em;
  flex-shrink: 0;
}
</style>
